import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { SelectOption } from '~/shared/utils/types';

import { apiFilterKey } from '../../../common/serachParamKeys';

interface ParsedFilters {
  active?: boolean[];
  ruleIds?: string[];
  accountIds?: string[];
}

interface FilterState {
  filters: ParsedFilters;
  appliedRuleIDFilters: SelectOption[];
  appliedAccountFilters: SelectOption[];
  appliedAutoscaleState: string[];
}

export function useAutoscaleRulesFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  const filterState: FilterState = useMemo(() => {
    const apiFilters = searchParams.get(apiFilterKey);

    const currentFilters = JSON.parse(apiFilters || '[]') as SelectOption[];
    const ruleIdFilters = currentFilters.filter((f) => f.group === 'ruleId');
    const accountFilters = currentFilters.filter((f) => f.group === 'accountId');
    const activeStateFilters = currentFilters.filter((f) => f.group === 'activeRuleState');

    const parsedFilters: ParsedFilters = {};

    if (ruleIdFilters.length > 0) {
      parsedFilters.ruleIds = ruleIdFilters.map((f) => f.id);
    }

    if (accountFilters.length > 0) {
      parsedFilters.accountIds = accountFilters.map((f) => f.id);
    }

    if (activeStateFilters.length > 0 && activeStateFilters.some((f) => f.id === 'active')) {
      parsedFilters.active = [true];
    }

    return {
      filters: parsedFilters,
      appliedRuleIDFilters: ruleIdFilters,
      appliedAccountFilters: accountFilters,
      appliedAutoscaleState: parsedFilters.active ? ['active'] : [],
    };
  }, [searchParams]);

  const handleResetFilters = useCallback(() => {
    setSearchParams(
      (prev) => {
        const updated = new URLSearchParams(prev);
        updated.delete(apiFilterKey);
        return updated;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  const handleApplyFilters = useCallback(
    (newFilters: ParsedFilters) => {
      setSearchParams(
        (prev) => {
          const updated = new URLSearchParams(prev);

          // Build SelectOption array for rule IDs
          const ruleIdOptions: SelectOption[] =
            newFilters.ruleIds?.map((id) => ({
              id,
              name: id,
              group: 'ruleId' as const,
            })) || [];

          // Build SelectOption array for account IDs
          const accountOptions: SelectOption[] =
            newFilters.accountIds?.map((id) => ({
              id,
              name: id,
              group: 'accountId' as const,
            })) || [];

          // Build SelectOption for active state
          const activeStateOptions: SelectOption[] =
            newFilters.active && newFilters.active.length > 0
              ? [{ id: 'active', name: 'Active', group: 'activeRuleState' as const }]
              : [];

          // Combine with existing filters (excluding ruleId, accountId, and activeRuleState)
          const existingFilters = JSON.parse(prev.get(apiFilterKey) || '[]') as SelectOption[];
          const otherFilters = existingFilters.filter(
            (f) => f.group !== 'ruleId' && f.group !== 'accountId' && f.group !== 'activeRuleState'
          );
          const finalFilters = [...otherFilters, ...ruleIdOptions, ...accountOptions, ...activeStateOptions];

          // Update api_filters parameter
          if (finalFilters.length > 0) {
            updated.set(apiFilterKey, JSON.stringify(finalFilters));
          } else {
            updated.delete(apiFilterKey);
          }

          return updated;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const apiFilters = searchParams.get(apiFilterKey);

  return {
    ...filterState,
    apiFilters,
    handleResetFilters,
    handleApplyFilters,
  };
}
